import { JSX, MouseEvent, useContext, useEffect, useState } from "react";

import { ProjectService } from "@app/projects";
import { AdequacyAggregate } from "@app/projects/network-assessment/adequacy";
import { FilteredResource, Resource, ResourceStatus } from "@app/resources";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { preventDefaultMouseEvent } from "@app/shared/utility";
import { ColumnConfig, SortDirections, Waffles } from "@app/shared/waffles";
import { numberSort } from "@app/shared/waffles/exports";
import MembershipRiskProjectTable from "./membershipRiskProjectTable";
import { FlyoutContext } from "@app/shared/Flyouts";

interface MembershipRiskProps {
  filteredResources: FilteredResource[];
  onClose?: () => void;
  projectResources: Resource[];
  showingArchived: boolean;
}

interface MembershipRiskProjectDetails {
  countyName: string;
  filterPath: string;
  projectIds: number[];
}

interface RiskyWaffleToppings extends MembershipRiskProjectDetails {
  key: string;
  countyNameLink: JSX.Element;
  projects: number;
  gaps: number;
  membershipAtRisk: string;
  totalMembership: string;
  membershipRiskPercent: string;
}

const columnConfig: ColumnConfig = {
  countyNameLink: {
    displayText: "County",
    testId: "county",
  },
  gaps: {
    displayText: "Gaps #",
    testId: "gaps",
  },
  membershipAtRisk: {
    displayText: "Membership At Risk #",
    testId: "at-risk-number",
  },
  membershipRiskPercentage: {
    displayText: "Membership At Risk %",
    testId: "at-risk-percent",
  },
  projects: {
    displayText: "Projects #",
    testId: "projects",
  },
  totalMembership: {
    displayText: "Total Membership #",
    testId: "total-membership",
  },
};

const MembershipRiskTable = (props: MembershipRiskProps): JSX.Element => {
  const [data, setData] = useState<RiskyWaffleToppings[]>(null);
  const [selectedWaffleToppings, setSelectedWaffleToppings] = useState<MembershipRiskProjectDetails>(null);
  const flyoutContext = useContext(FlyoutContext);

  const coerceData = (aggregateData: AdequacyAggregate[]) => {
    /* eslint-disable sort-keys */
    return aggregateData.map((item) => {
      const membershipRiskProjectDetails = {
        countyName: `${item.regionSetDisplayName}, ${item.stateDisplayName}`,
        filterPath: item.filterPath,
        projectIds: item.membershipRiskProjectIds,
      };
      return ({
        key: item.regionSetDisplayName,
        filterPath: item.filterPath,
        projectIds: membershipRiskProjectDetails.projectIds,
        countyName: membershipRiskProjectDetails.countyName,
        countyNameLink: (
          <a href="#" onClick={(e: MouseEvent) => preventDefaultMouseEvent(e, () => setSelectedWaffleToppings(membershipRiskProjectDetails))}>
            {membershipRiskProjectDetails.countyName}
          </a>
        ),
        projects: item.projectCount,
        gaps: item.gapCount,
        membershipAtRisk: item.membershipRiskCount.toLocaleString(),
        totalMembership: item.membershipRiskServiceAreaCount.toLocaleString(),
        membershipRiskPercent: `${item.percentMembershipRisk && item.percentMembershipRisk.toFixed(1)}%`,
      });

    });
    /* eslint-enable sort-keys */
  };

  useEffect(() => {
    const projectService = new ProjectService();

    projectService.getRegionSetAggregates(props.filteredResources, props.showingArchived ? ResourceStatus.Archived : ResourceStatus.Active, null, null, true).then((response) => {
      const properData = coerceData(response);
      setData(properData);
    });
  }, []);

  useEffect(() => {
    if (!!selectedWaffleToppings) {
      flyoutContext.setTitle(`Projects with Membership at Risk in ${selectedWaffleToppings.countyName}`)
    } else {
      flyoutContext.setTitle("Counties with Membership at Risk")
    }
  }, [selectedWaffleToppings]);

  const customSort = (segment: RiskyWaffleToppings[], column: keyof RiskyWaffleToppings, sortDirection: SortDirections) => {
    if (sortDirection === SortDirections.None) {
      return data;
    }
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;
    switch (column) {
      case "countyNameLink":
        return segment.sort((a, b) => (a.countyName > b.countyName ? 1 : -1) * multiplier);
      case "projects":
      case "gaps":
        return segment.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      case "membershipAtRisk":
      case "membershipRiskPercent":
      case "totalMembership":
        return numberSort<RiskyWaffleToppings>(segment, column, multiplier);
      default:
        console.warn(`There's no function to handle sorting column ${column}.`);
    }
  };

  if (selectedWaffleToppings) {
    return (
      <MembershipRiskProjectTable
        countyName={selectedWaffleToppings.countyName}
        filterPath={selectedWaffleToppings.filterPath}
        onBreadCrumbClick={() => setSelectedWaffleToppings(null)}
        projectIds={selectedWaffleToppings.projectIds}
        projectResources={props.projectResources}
      />
    );
  } else {
    return (
      <>
        {data ?
          <Waffles<RiskyWaffleToppings>
            columns={columnConfig}
            customSort={customSort}
            data={data}
            testId="membership-risk-table"
          /> :
          <LoadingSpinner />}
      </>
    );
  }

};

export default MembershipRiskTable;
